const _ = require("lodash");
const moment = require("moment");
const Big = require("big.js");
const ErrorUtil = require("../../../shared/ErrorUtil");
const AT = require("../../core/vault/ActionTypes");
const LoadState = require("../../core/vault/util/LoadState");
const {setIn, extendIn} = require("../../../shared/ImmHelpers");
const CouponUtil = require("../../../shared/CouponUtil");
const StoreUtil = require("../../../shared/StoreUtil");
const SubscriptionUtil = require("../../../shared/SubscriptionUtil");
const ShopData = require("../../../shared/data/dbShop.json");
const StoreBundleUtil = require("../../../shared/StoreBundleUtil");
const OpsCheckoutForm = require("../../office/vault/OpsCheckoutForm");
const {
	markForReload,
	getBadDisplayName,
	canAddStudent,
	canActivateStudent,
	getStudentCursor,
	getPlatformStudent,
	isStudentInActiveClasses,
} = require("./OpsParentAcc");
const {DEFAULT_BUNDLE_GRADE} = require("../../../shared/Constants");

const DEFAULT_PLAN_TYPE_IS_BLANK = true;

// TODO: computations to try to share with backend
// - immediate price
// - renewal price
// - expiration
// - auto-renew

/*
These functions mostly deal with parentAcc.subChangeState, but all of these
functions operate on the full parentAcc since we often need other data about
the parentAcc for these computations. See OpsParentAcc for the parentAcc shape.

SubChange shape {
  studentID: kept in sync with page,
  changeType: kept in sync with page,
  newDisplayName: for type "add" only,
  displayNameError: null or string,
  newPlanType: "monthly" | "yearly" | "bundle",
  bundleGrades,
  agreedToBilling: true|false,
  form: OpsCheckoutForm object though only shipping is used,
  priceInfo: {
    generation: number that increases each full price reset, attached to
      payloads and responses to ensure late responses don't get used,
    shipping: address used to fetch prices, only updated on review step or when
      addresses on file for the user change,
    forPlanType: {
      [key]: SubChangePrices object if loaded,
      (key is "monthly", "yearly", or the different bundles that we have, e.g. "bundle-grade-3")
    },
  },
	students: [{various student info and subscription info}]
  usingAccountAddress: true|false,
  shippingIsNewDefault: true|false,
  shippingSavePending: true|false,
  shippingVerifyPending: true|false,
  shippingVerified: true|false,
  isRequestPending: true|false,
}

SubChangePrices shape {
  load,
  loadError,
  items: null | Array of {sku, quantity, options, price}
  totals: null | {subtotal, credit, shipping, tax, total},
  expiresAt: ISO-format date that subscription will expire/renew,
}
*/

// Also doubles as initialization. parentAcc.subChangeState only exists when the
// user is on the page.
// The parentAcc comes from lib/modules/subscription/api/get.js#L13
// and then processed through src/parent/vault/OpsParentAcc.js#L127 and src/parent/vault/UpdateParent.js#L1124
const syncToPage = function(parentAcc, page, api, parentData) {
	if (page.name !== "parent.subscribe") {
		// If there happens to be a subChangeState from a previous subChange, clear it. Don't need to actually setup anything.
		if (parentAcc && parentAcc.subChangeState) {
			return setIn(parentAcc, "subChangeState", null);
		}
		return parentAcc;
	} else if (
		parentAcc.subChangeState &&
		!parentAcc.subChangeState.error &&
		parentAcc.subChangeState.studentID === page.studentID &&
		parentAcc.subChangeState.changeType === page.changeType
	) {
		// We're moving between checkout states
		parentAcc = updateShippingMethod(parentAcc);
		// If we're on the review step, we should update shipping.
		if (page.step === "review" && needsToUpdatePricesShipping(parentAcc)) {
			parentAcc = resetLoadedPrices(parentAcc, api, {updateShipping: true});
		}

		return parentAcc;
	}

	// Just starting checkout, see if we are good to go
	const studentCR = getStudentCursor(parentAcc, page.studentID);
	const isChange =
		page.changeType !== "reactivate" && page.changeType !== "add";
	// The errors here should not occur in normal use of the page, but if the
	// user bookmarks a URL in the change subscription flow or something like
	// that, they are possible. At the time of writing, we show a 404 for these,
	// so the actual message is ignored.
	const setError = (error, redirect) => {
		// Redirect used by UpdateParentAcc to send user to account page to see
		// error messaging and/or provide call to action to fix fixable issues
		parentAcc = resetState(parentAcc, page.studentID, page.changeType);
		return extendIn(parentAcc, "subChangeState", {
			studentID: page.studentID,
			changeType: page.changeType,
			error,
			redirect: !!redirect,
		});
	};

	if (!parentAcc.shownCC) {
		return setError("No billing information on file", true);
	} else if (page.changeType !== "add" && !studentCR.exists()) {
		return setError("Changing a nonexistent subscription");
	} else if (
		page.changeType === "reactivate" &&
		studentCR.getIn(["subscription", "active"])
	) {
		return setError("Reactivating an already-active subscription");
	} else if (isChange && !studentCR.getIn(["subscription", "active"])) {
		return setError("Changing an inactive subscription; must reactivate first");
	} else if (isChange && studentCR.getIn(["subscription", "purchaseOrderID"])) {
		return setError("Cannot change types of purchase orders.");
	} else if (page.changeType === "add" && !canAddStudent(parentAcc)) {
		return setError(
			"Too many subscriptions on this account; can't add another",
			true
		);
	} else if (
		page.changeType === "reactivate" &&
		!canActivateStudent(parentAcc)
	) {
		return setError(
			"Too many subscriptions on this account; can't activate another",
			true
		);
	} else if (
		page.studentID &&
		page.changeType === "reactivate" &&
		isStudentInActiveClasses(parentAcc, page.studentID, parentData)
	) {
		return setError("studentInActiveClasses");
	} else {
		// No errors! Lets set up the subChangeState inside the parent object as they enter checkout
		parentAcc = resetState(parentAcc, page.studentID, page.changeType);
		parentAcc = loadPricesIfNeeded(parentAcc, api);
	}
	return parentAcc;
};

// Internal helper. This sets the initial parentAcc and subChangeState when you enter the checkout flow.
const resetState = function(parentAcc, studentID, changeType) {
	let newDisplayName = "";
	if (changeType === "add" && studentID) {
		// If we have a studentID to add a sub for, lets pull their displayName from platformStudents
		const platformStudent = getPlatformStudent(parentAcc, studentID);
		newDisplayName = platformStudent?.displayName;
	}

	const studentCR = getStudentCursor(parentAcc, studentID);
	// init here takes a now field, but is only used for the creditCard
	// section. We only need this form for the shipping section.
	let form = OpsCheckoutForm.init("subChange");
	form = OpsCheckoutForm.setData(
		form,
		"shipping",
		getShippingOnFile(parentAcc)
	);
	parentAcc = setIn(parentAcc, "subChangeState", {
		studentID,
		changeType,
		newDisplayName: newDisplayName,
		displayNameError: null,
		newPlanType: getDefaultPlanType(parentAcc, changeType, studentCR),
		bundleGrades: [],
		bookLevelDeferred: false,
		agreedToBilling: false,
		form,
		priceInfo: {
			generation: 1,
			shipping: null,
			forPlanType: {},
		},
		usingAccountAddress: true,
		shippingIsNewDefault: true,
		shippingSavePending: false,
		shippingVerifyPending: false,
		shippingVerified: false,
		isRequestPending: false,
	});
	parentAcc = updateShippingMethod(parentAcc);
	parentAcc = setIn(
		parentAcc,
		["subChangeState", "priceInfo", "shipping"],
		getShippingWithMethod(parentAcc)
	);

	return parentAcc;
};

// Returns {types, current}. types is an array of plan types to make radio
// buttons for. If current is returned and also in types, then that radio
// button should be rendered noninteractive.
const getValidPlanTypes = function(parentAcc) {
	const scs = parentAcc.subChangeState;
	if (!scs) return {types: [], current: null};
	if (scs.changeType === "renew") {
		return {types: ["bundle"], current: null};
	}
	const types = ["bundle", "yearly", "monthly"];
	let current;
	const studentCR = getStudentCursor(parentAcc, scs.studentID);
	if (scs.changeType === "upgrade" && studentCR.exists()) {
		current = studentCR.getIn(["subscription", "planType"]);
		if (current === "monthly") {
			return {types, current};
		} else if (current === "yearly") {
			return {types: ["bundle", "yearly"], current};
		}
	} else if (scs.changeType === "switch" && studentCR.exists()) {
		current = studentCR.getIn(["subscription", "planType"]);
		return {types, current};
	} else {
		return {types, current: null};
	}
};

// Internal helper
const getDefaultPlanType = function(parentAcc, changeType, studentCR) {
	if (changeType === "renew" || changeType === "upgrade") {
		return "bundle";
	} else if (DEFAULT_PLAN_TYPE_IS_BLANK) {
		return null;
	}
	const hasActive = type =>
		parentAcc.students.some(s => {
			return s.subscription.active && s.subscription.planType === type;
		});
	const current =
		changeType === "switch" && studentCR.exists()
			? studentCR.getIn(["subscription", "planType"])
			: null;
	let defaultType;
	if (hasActive("yearly")) {
		defaultType = "yearly";
	} else if (hasActive("monthly")) {
		defaultType = "monthly";
	} else if (current) {
		defaultType = current;
	} else {
		defaultType = "yearly";
	}
	if (defaultType === current) {
		defaultType = current === "yearly" ? "monthly" : "yearly";
	}
	return defaultType;
};

const needsShipping = function(parentAcc) {
	const scs = parentAcc.subChangeState;
	return scs && scs.newPlanType === "bundle";
};

const setUsingAccountAddress = function(parentAcc, value) {
	parentAcc = setIn(
		parentAcc,
		["subChangeState", "usingAccountAddress"],
		value
	);
	parentAcc = updateShippingMethod(parentAcc);
	return parentAcc;
};

const getShippingOnFile = function(parentAcc) {
	const typeOnFile = parentAcc.hasShipping ? "shipping" : "billing";
	const scs = parentAcc.subChangeState;
	let method = "";
	let comments = "";
	if (scs && scs.form) {
		const scsForm = OpsCheckoutForm.getSection(scs.form, "shipping");
		method = scsForm.method;
		comments = scsForm.comments;
	}
	const storedFormWithMethod = OpsCheckoutForm.setData(
		parentAcc.storedForm,
		typeOnFile,
		{method, comments}
	);
	return OpsCheckoutForm.getSection(storedFormWithMethod, typeOnFile);
};

const getShipping = function(parentAcc) {
	const scs = parentAcc.subChangeState;
	if (!scs) {
		return null;
	} else if (scs.usingAccountAddress) {
		return getShippingOnFile(parentAcc);
	} else {
		return OpsCheckoutForm.getSection(scs.form, "shipping");
	}
};

const needsShippingToBlockedAddress = function(parentAcc) {
	const shipping = getShipping(parentAcc) || {};
	return (
		isNewBundlePlanType(parentAcc) && !StoreUtil.isAddressShippable(shipping)
	);
};

// Used by backend, which has to be passed a shipping method.
const getShippingWithMethod = function(parentAcc) {
	const shipping = getShipping(parentAcc);
	if (shipping.method) return shipping;
	const address = getShipping(parentAcc);
	const method =
		StoreUtil.getDefaultShippingType(address.country) || "us-economy";
	return setIn(shipping, "method", method);
};

// Internal helper.
const computeUpdatedShippingMethod = function(shipping) {
	const oldMethod = shipping.method;
	let newMethod = oldMethod;
	const defaultMethod = StoreUtil.getDefaultShippingType(shipping.country);
	if (defaultMethod) {
		newMethod = defaultMethod;
	} else if (StoreUtil.requiresPriorityShipping(shipping.state)) {
		newMethod = "us-priority";
	} else if (oldMethod === "canada" || oldMethod === "international") {
		newMethod = "";
	}
	return newMethod;
};

const updateShippingMethod = function(parentAcc) {
	const shipping = getShipping(parentAcc);
	const oldMethod = shipping.method;
	const newMethod = computeUpdatedShippingMethod(shipping);
	if (oldMethod === newMethod) return parentAcc;
	return editShipping(parentAcc, {method: newMethod});
};

const editShipping = function(parentAcc, values) {
	const form = parentAcc.subChangeState.form;
	const {form: newForm, responseFlags} = OpsCheckoutForm.updateData(
		form,
		"shipping",
		values
	);
	if (responseFlags.hadUpdates) {
		parentAcc = setIn(parentAcc, ["subChangeState", "form"], newForm);
		// This if may not be needed, but guaranteeing no call stack overflow here.
		if (values.country || values.state) {
			parentAcc = updateShippingMethod(parentAcc);
		}
	}
	if (responseFlags.verifyAddress) {
		parentAcc = setIn(parentAcc, ["subChangeState", "shippingVerified"], false);
	}
	return parentAcc;
};

// Internal helper to help estimate shipping price in getPrices.
const calculateShippingPriceWithItems = function(parentAcc, items, method) {
	if (!needsShipping(parentAcc)) return "0.00";
	const shipping = getShipping(parentAcc);
	method = method || (shipping && shipping.method);
	if (!method) {
		return null;
	}
	return StoreUtil.calculateShipping(items, method).toFixed(2);
};

const calculateShippingPriceWithMethod = function(parentAcc, method) {
	let prices = getPrices(parentAcc);
	if (!prices) {
		prices = computeBasePrices(parentAcc, true);
		if (!prices) return null;
	}
	return calculateShippingPriceWithItems(parentAcc, prices.items, method);
};

const validateShippingWithMissing = function(parentAcc) {
	const scs = parentAcc.subChangeState;
	const options = {addMissing: true, methodOnly: scs.usingAccountAddress};
	return setIn(parentAcc, ["subChangeState", "form"], f => {
		return OpsCheckoutForm.validateSection(f, "shipping", options);
	});
};

const checkShipping = function(parentAcc) {
	const scs = parentAcc.subChangeState;
	const options = {methodOnly: scs.usingAccountAddress};
	return OpsCheckoutForm.checkSection(scs.form, "shipping", options);
};

const isShippingComplete = function(parentAcc) {
	const scs = parentAcc.subChangeState;
	if (scs.usingAccountAddress) {
		return !!OpsCheckoutForm.getSection(scs.form, "shipping").method;
	}
	return OpsCheckoutForm.isSectionComplete(scs.form, "shipping");
};

// Frontend should not provide an option if this returns true.
const mustSaveShippingAsDefault = function(parentAcc) {
	return !parentAcc.hasShipping;
};

const shouldSaveShippingAsDefault = function(parentAcc) {
	const scs = parentAcc.subChangeState;
	if (!scs) return false;
	return (
		(!scs.usingAccountAddress && scs.shippingIsNewDefault) ||
		mustSaveShippingAsDefault(parentAcc)
	);
};

const setShippingIsNewDefault = function(parentAcc, value) {
	return setIn(parentAcc, ["subChangeState", "shippingIsNewDefault"], value);
};

const needsToVerifyShipping = function(parentAcc) {
	const scs = parentAcc.subChangeState;
	if (scs.usingAccountAddress || scs.shippingVerified) return false;
	const shipping = getShipping(parentAcc);
	return StoreUtil.canVerifyAddress(shipping);
};

// Call when going past shipping step. Returns an action or null. Null means to
// move onto the next step right away. If an action is returned, dispatch it
// instead.
const getFinishShippingAction = function(parentAcc) {
	if (shouldSaveShippingAsDefault(parentAcc)) {
		const shipping = getShipping(parentAcc);
		return {
			type: AT.SAVE_PARENT_FORM_REQUEST,
			section: "shipping",
			vals: shipping,
			forSubChange: true,
		};
	} else if (needsToVerifyShipping(parentAcc)) {
		return {type: AT.SUBSCRIPTION_VERIFY_SHIPPING_REQUEST};
	}
	return null;
};

const shouldBlockPage = function(parentAcc) {
	const scs = parentAcc.subChangeState;
	return (
		scs &&
		(scs.shippingSavePending ||
			scs.shippingVerifyPending ||
			scs.isRequestPending)
	);
};

// A hacky hook into the save shipping action, because a bunch of extra things
// need to happen when it succeeds. If the request action has forSubChange:
// true, this function is called with "pending". It is always called with
// "done" or "error" on any save parent form response, but these are only
// processed given a previous "pending" call.
const setShippingSavingState = function(
	parentAcc,
	emitEvent,
	status,
	statusInfo
) {
	const scs = parentAcc.subChangeState;
	if (!scs) {
		// Do nothing
	} else if (status === "pending") {
		// Note this flag, in addition to making this function work, makes the view
		// show a loading indicator.
		parentAcc = setIn(
			parentAcc,
			["subChangeState", "shippingSavePending"],
			true
		);
	} else if (scs.shippingSavePending && status === "done") {
		const oldForm = OpsCheckoutForm.getSection(scs.form, "shipping");
		let newForm = OpsCheckoutForm.setData(
			scs.form,
			"shipping",
			getShippingOnFile(parentAcc)
		);
		newForm = OpsCheckoutForm.setData(newForm, "shipping", {
			method: oldForm.method,
		});
		newForm = OpsCheckoutForm.setSectionInvalidAddress(
			newForm,
			"shipping",
			false
		);

		parentAcc = extendIn(parentAcc, ["subChangeState"], {
			shippingSavePending: false,
			form: newForm,
			usingAccountAddress: true,
			shippingIsNewDefault: true,
		});
		parentAcc = updateShippingMethod(parentAcc);
		emitEvent("parentNewDefaultShippingSuccess");
	} else if (scs.shippingSavePending && status === "error") {
		parentAcc = setIn(
			parentAcc,
			["subChangeState", "shippingSavePending"],
			false
		);
		if (statusInfo === "E_ADDRESS_NOT_VERIFIED") {
			emitEvent("checkoutFieldsError");
			parentAcc = setIn(parentAcc, ["subChangeState", "form"], f => {
				return OpsCheckoutForm.setSectionInvalidAddress(f, "shipping", true);
			});
		} else {
			emitEvent("parentNewDefaultShippingFail");
		}
	}
	return parentAcc;
};

const doVerifyShippingRequest = function(parentAcc, api) {
	parentAcc = setIn(
		parentAcc,
		["subChangeState", "shippingVerifyPending"],
		true
	);
	const shipping = getShipping(parentAcc);
	api.subVerifyAddress(shipping);
	return parentAcc;
};

const doVerifyShippingSuccess = function(parentAcc, emitEvent, response) {
	parentAcc = setIn(
		parentAcc,
		["subChangeState", "shippingVerifyPending"],
		false
	);
	if (response.verified) {
		parentAcc = setIn(parentAcc, ["subChangeState", "shippingVerified"], true);
		parentAcc = setIn(parentAcc, ["subChangeState", "form"], f => {
			f = OpsCheckoutForm.setSectionInvalidAddress(f, "shipping", false);
			return OpsCheckoutForm.setVerifiedAddress(
				f,
				"shipping",
				response.verifiedAddress
			);
		});
		emitEvent("parentVerifyAddressSuccess");
	} else {
		emitEvent("checkoutFieldsError");
		parentAcc = setIn(parentAcc, ["subChangeState", "form"], f => {
			return OpsCheckoutForm.setSectionInvalidAddress(f, "shipping", true);
		});
	}
	return parentAcc;
};

const doVerifyShippingFail = function(parentAcc, emitEvent, error) {
	parentAcc = setIn(
		parentAcc,
		["subChangeState", "shippingVerifyPending"],
		false
	);
	emitEvent("parentVerifyAddressFail");
	return parentAcc;
};

const needsPlanType = function(parentAcc) {
	const scs = parentAcc.subChangeState;
	return scs && !scs.newPlanType;
};

const setDisplayName = function(parentAcc, emitEvent, value) {
	// Assumes this is only called for changeType add.
	const bad = getBadDisplayName(parentAcc, value);
	const error = bad && bad.name === value ? bad.error : null;
	return extendIn(parentAcc, ["subChangeState"], {
		newDisplayName: value,
		displayNameError: error,
	});
};

const getDisplayName = function(parentAcc) {
	const scs = parentAcc.subChangeState;
	if (!scs) return null;
	if (!scs.studentID || scs.changeType === "add") return scs.newDisplayName;
	const studentCR = getStudentCursor(parentAcc, scs.studentID);
	return studentCR.getIn("displayName");
};

const canSetDisplayName = function(parentAcc) {
	const scs = parentAcc.subChangeState;
	return scs && scs.changeType === "add";
};

const needsDisplayName = function(parentAcc) {
	const scs = parentAcc.subChangeState;
	return (
		scs &&
		canSetDisplayName(parentAcc) &&
		(scs.displayNameError || !scs.newDisplayName)
	);
};

const setPlanType = function(parentAcc, value) {
	return setIn(parentAcc, ["subChangeState", "newPlanType"], value);
};

const setBundleGrades = function(parentAcc, value) {
	return setIn(parentAcc, ["subChangeState", "bundleGrades"], value);
};

const setBookLevelDeferred = function(parentAcc, value) {
	return setIn(parentAcc, ["subChangeState", "bookLevelDeferred"], !!value);
};

const getBookLevelDeferred = function(parentAcc) {
	return !!_.get(parentAcc, ["subChangeState", "bookLevelDeferred"]);
};

const needsBundleGrades = function(parentAcc) {
	const scs = parentAcc.subChangeState;
	return scs && scs.newPlanType === "bundle" && !scs.bundleGrades.length;
};

const setAgreedToBilling = function(parentAcc, value) {
	return setIn(parentAcc, ["subChangeState", "agreedToBilling"], value);
};

const canAgreeToBilling = function(parentAcc) {
	const scs = parentAcc.subChangeState;
	return scs && scs.newPlanType !== "bundle";
};

const needsToAgreeToBilling = function(parentAcc) {
	const scs = parentAcc.subChangeState;
	return scs && canAgreeToBilling(parentAcc) && !scs.agreedToBilling;
};

// Returns true if the subscription currently being changed is refundable
const canCancelSub = function(parentAcc) {
	const scs = parentAcc.subChangeState;
	if (!scs) return null;
	if (!scs.studentID) return true;
	const studentCR = getStudentCursor(parentAcc, scs.studentID);
	return studentCR.getIn(["subscription", "refundable"]);
};

const getCurrentSubscription = function(parentAcc) {
	const scs = parentAcc.subChangeState;
	if (!scs || !scs.studentID) return null;
	const studentCR = getStudentCursor(parentAcc, scs.studentID);
	return studentCR.getIn("subscription");
};

const isNewBundlePlanType = function(parentAcc) {
	const scs = _.get(parentAcc, "subChangeState");
	if (scs) {
		const newPlanType = _.get(scs, "newPlanType");
		return newPlanType === "bundle";
	}
	return false;
};

const emptyTotals = {
	subtotal: "0.00",
	couponApplied: "0.00",
	shipping: "0.00",
	tax: "0.00",
	total: "0.00",
};

// Internal helper. Takes {items, totals} and fixes totals.shipping and
// totals.total if needed.
// TODO: For some reason the backend isn't computing the shipping value, which
// is one reason this exists. This might break the price checking...
const fixPrices = function(parentAcc, prices) {
	const {items, totals} = prices;
	const realShipping = calculateShippingPriceWithItems(parentAcc, items);
	const realTotal = Big(totals.subtotal)
		.sub(totals.couponApplied || "0")
		.add(realShipping || "0")
		.add(totals.tax || "0")
		.toFixed(2);
	return extendIn(prices, ["totals"], {
		shipping: realShipping,
		total: realTotal,
	});
};

// Internal helper. Same as an OpsCart function.
const computeCouponApplied = function(items, coupon) {
	if (!coupon) return "0.00";
	const totals = StoreUtil.getTotalItemPrices(items, coupon);
	return totals.applied.toFixed(2);
};

// Applies or clears bookLevelDeferred on yearly bundle lines from subChangeState.
const stampDefermentOnItems = function(parentAcc, items) {
	if (!items) {
		return items;
	}
	const deferred = getBookLevelDeferred(parentAcc);
	return items.map(item => {
		if (item.sku !== "beast-yearly") {
			return item;
		}
		if (!deferred) {
			if (!item.options.bookLevelDeferred) {
				return item;
			}
			// strip the bookLevelDeferred and provisionalGrade options
			return {
				...item,
				options: _.omit(item.options, [
					"bookLevelDeferred",
					"provisionalGrade",
				]),
			};
		}
		const grade =
			item.options.provisionalGrade ??
			StoreBundleUtil.getBundleGrade(item.options.bundleID);
		return {
			...item,
			options: {
				...item.options,
				bookLevelDeferred: true,
				provisionalGrade: grade,
			},
		};
	});
};

// Internal helper. Computes items to show to frontend in a price computation.
const computeItems = function(parentAcc, planType, subCost) {
	const scs = parentAcc.subChangeState;
	if (!scs) {
		return null;
	} else if (planType === "bundle") {
		const displayName = getDisplayName(parentAcc);
		const bundleGrades = getBundleGrades(parentAcc, true);
		const items = _.flatMap(bundleGrades, g =>
			StoreBundleUtil.makeItems(g, displayName)
		);
		items.forEach(i => {
			i.price = Big(ShopData[i.sku].subscriptionBundlePrice).toFixed(2);
		});
		return stampDefermentOnItems(parentAcc, items);
	} else {
		const sku = `beast-${planType}`;
		const displayName = getDisplayName(parentAcc);
		if (!subCost) {
			const isMulti = parentAcc.students.some(s => {
				const sub = s.subscription;
				return sub && sub.active && sub.planType !== "monthly";
			});
			subCost = Big(
				isMulti ? ShopData[sku].multiPrice : ShopData[sku].price
			).toFixed(2);
		}
		const item = {sku, quantity: 1, options: {displayName}, price: subCost};
		return [item];
	}
};

// Internal helper. Best possible computation of {items, totals} prices without
// backend help. shipping and tax may be null if backend help needed. Will
// return null if it has no idea (or is an upgrade)
// This needs to be kept in careful sync with price logic on the backend.
// If force = true, force calculate wrong base prices for upgrade
// TODO: or can we do better sharing? Not sure we can...
const computeBasePrices = function(parentAcc, force) {
	const scs = parentAcc.subChangeState;
	const isNew = scs.changeType === "add" || scs.changeType === "reactivate";
	if (!scs || !scs.newPlanType || (scs.changeType === "upgrade" && !force)) {
		return null;
	} else if (scs.newPlanType === "monthly") {
		//No idea; need prorated info
		return null;
	} else if (!isNew && scs.newPlanType === "yearly") {
		// Never charges
		return {
			items: [],
			totals: emptyTotals,
		};
	} else {
		const items = computeItems(parentAcc, scs.newPlanType);
		const couponApplied = computeCouponApplied(items, scs.coupon);
		const subtotal = items
			.reduce((bigTotal, item) => {
				return bigTotal.add(item.price);
			}, Big(0))
			.toFixed(2);
		return fixPrices(parentAcc, {
			items,
			totals: _.extend({}, emptyTotals, {
				subtotal,
				couponApplied,
				tax: null,
			}),
		});
	}
};

// Internal helper.
const computeBaseExpiresAt = function(parentAcc) {
	const scs = parentAcc.subChangeState;
	const isNew = scs.changeType === "add" || scs.changeType === "reactivate";
	// Date to add 1 month / 1 year / N years to for computing subscription.
	const baseDate = isNew
		? moment.utc()
		: moment.utc(
				getStudentCursor(parentAcc, scs.studentID).getIn([
					"subscription",
					"expiresAt",
				])
		  );
	if (!scs || !scs.newPlanType) {
		return null;
	} else if (scs.newPlanType === "bundle") {
		return baseDate.add(scs.bundleGrades.length, "years").toISOString();
	} else if (!isNew) {
		return baseDate;
	} else if (scs.newPlanType === "yearly") {
		// TODO: leap years might desync this from backend, share more?
		return baseDate.add(1, "years").toISOString();
	} else if (scs.newPlanType === "monthly") {
		while (baseDate.date() >= 29) baseDate.add(1, "days");
		return baseDate.add(1, "months").toISOString();
	}
};

// Internal helper. Adds displayNames to items returned from backend.
const addDisplayNames = function(parentAcc, items) {
	const displayName = getDisplayName(parentAcc);
	return items.map(i => {
		if (StoreUtil.getItemType(i) === "subscription") {
			return setIn(i, ["options", "displayName"], displayName);
		}
		return i;
	});
};

// Internal helper. Pass return of computeBasePrices if you have it; else it
// will be computed here. Returns true if no backend call is needed.
const areBasePricesComplete = function(parentAcc, basePrices) {
	basePrices = basePrices || computeBasePrices(parentAcc);
	return basePrices && basePrices.totals.tax !== null;
};

const getBundleGrades = function(parentAcc, useDefaultBundleGrade = false) {
	const scs = parentAcc.subChangeState;
	const defaultBundleGrades = useDefaultBundleGrade
		? [DEFAULT_BUNDLE_GRADE]
		: [];
	const bundleGrades = scs.bundleGrades?.length
		? scs.bundleGrades
		: defaultBundleGrades;
	return scs && scs.newPlanType === "bundle" ? bundleGrades : [];
};

// Internal helper
const getPricesCurrLoadKey = function(parentAcc) {
	const scs = parentAcc.subChangeState;
	if (!scs || !scs.newPlanType) return null;
	return getPricesLoadKey(scs.newPlanType, getBundleGrades(parentAcc));
};

// Internal helper
const getPricesCurrInfo = function(parentAcc) {
	const scs = parentAcc.subChangeState;
	const k = getPricesCurrLoadKey(parentAcc);
	if (!scs || !k) return parentAcc;
	return scs.priceInfo.forPlanType[k];
};

const getPricesLoadKey = function(planType, bundleGrades) {
	let key = planType;
	if (planType === "bundle") {
		if (!bundleGrades?.length) {
			bundleGrades = [DEFAULT_BUNDLE_GRADE];
		}
		const sortedBundles = bundleGrades.sort();
		key += sortedBundles.join("");
	}
	return key;
};

const loadPricesIfNeeded = function(parentAcc, api) {
	const scs = parentAcc.subChangeState;
	const k = getPricesCurrLoadKey(parentAcc);
	if (!scs || !k) return parentAcc;
	const bundleGrades = getBundleGrades(parentAcc, true);
	const currInfo =
		scs.priceInfo.forPlanType[k] ||
		LoadState.loadInit({items: null, totals: null});
	const newInfo = LoadState.loadStart(currInfo, () => {
		const shipping = scs.priceInfo.shipping;
		const couponCode = getCouponCode(parentAcc);
		const generation = scs.priceInfo.generation;
		const studentCR = getStudentCursor(parentAcc, scs.studentID);
		const subscription = studentCR.getIn("subscription");
		const subscriptionID = studentCR.getIn(["subscription", "subscriptionID"]);
		const willExpireSoon = SubscriptionUtil.isActivePastExpiration(
			subscription
		);
		if (
			(scs.changeType === "switch" && !willExpireSoon) ||
			scs.changeType === "renew" ||
			scs.changeType === "upgrade"
		) {
			api.subGetSwitchTypeData({
				subscriptionID,
				grades: bundleGrades,
				shipping,
				couponCode,
				generation,
				changeType: scs.changeType,
			});
		} else {
			api.subGetPrices(
				scs.newPlanType,
				bundleGrades,
				shipping,
				couponCode,
				generation
			);
		}
	});
	return setIn(
		parentAcc,
		["subChangeState", "priceInfo", "forPlanType", k],
		newInfo
	);
};

const needsToUpdatePricesShipping = function(parentAcc) {
	const scs = parentAcc.subChangeState;
	if (!scs) return false;
	const oldShipping = scs.priceInfo.shipping;
	const currShipping = getShippingWithMethod(parentAcc);
	return !_.isEqual(oldShipping, currShipping);
};

const resetLoadedPrices = function(parentAcc, api, options = {}) {
	const {updateShipping = false} = options;
	// Unlike many functions here, this can be legitimately called when nothing
	// is happening with subscription changing, so this check is important.
	const scs = parentAcc.subChangeState;
	if (!scs) return parentAcc;
	parentAcc = setIn(parentAcc, ["subChangeState", "priceInfo"], old => ({
		generation: old.generation + 1,
		shipping: updateShipping ? getShippingWithMethod(parentAcc) : old.shipping,
		forPlanType: {},
	}));
	parentAcc = loadPricesIfNeeded(parentAcc, api);
	return parentAcc;
};

const setLoadedPricesSuccess = function(parentAcc, generation, payload, data) {
	const scs = parentAcc.subChangeState;
	if (!scs) return parentAcc;
	if (scs.priceInfo.generation > generation) return parentAcc;
	const k = getPricesLoadKey(payload.type, payload.grades);
	const newItems = computeItems(parentAcc, payload.type, data.totals.subtotal);
	const newInfo = LoadState.loadSuccess({
		items: newItems,
		totals: data.totals,
		expiresAt: data.expiresAt,
	});
	return setIn(
		parentAcc,
		["subChangeState", "priceInfo", "forPlanType", k],
		newInfo
	);
};

const setLoadedPricesFail = function(parentAcc, generation, payload, error) {
	const scs = parentAcc.subChangeState;
	if (!scs) return parentAcc;
	if (scs.priceInfo.generation > generation) return parentAcc;
	const k = getPricesLoadKey(payload.type, payload.grades);
	const newInfo = LoadState.loadError(
		scs.priceInfo.forPlanType[k] || {},
		error
	);
	return setIn(
		parentAcc,
		["subChangeState", "priceInfo", "forPlanType", k],
		newInfo
	);
};

const setLoadedSwitchTypePricesSuccess = function(
	parentAcc,
	generation,
	payload,
	data
) {
	const scs = parentAcc.subChangeState;
	if (!scs) return parentAcc;
	if (scs.priceInfo.generation > generation) return parentAcc;
	const newData = {};
	["monthly", "yearly", "bundle"].forEach(type => {
		if (!data[type]) return;
		const {cost, tax, shipping, expiresAt, couponApplied, items} = data[type];
		const k = getPricesLoadKey(type, payload.grades);
		// Use the items from backend (which includes price) if provided.
		// Backend only sends through items if it's an upgrade to bundle.
		// Backend will use default grade level, so it needs to be replaced once
		// user choses a real grade level.
		const newItems = items || computeItems(parentAcc, type, cost);
		newData[k] = LoadState.loadSuccess(
			fixPrices(parentAcc, {
				items: newItems,
				totals: {subtotal: cost, tax, shipping, couponApplied},
				expiresAt,
			})
		);
	});
	return extendIn(
		parentAcc,
		["subChangeState", "priceInfo", "forPlanType"],
		newData
	);
};

const setLoadedSwitchTypePricesFail = function(
	parentAcc,
	generation,
	payload,
	error
) {
	const scs = parentAcc.subChangeState;
	if (!scs) return parentAcc;
	if (scs.priceInfo.generation > generation) return parentAcc;
	const newData = {};
	["monthly", "yearly", "bundle"].forEach(type => {
		const k = getPricesLoadKey(type, payload.grades || []);
		newData[k] = LoadState.loadError(scs.priceInfo.forPlanType[k] || {}, error);
	});
	return extendIn(
		parentAcc,
		["subChangeState", "priceInfo", "forPlanType"],
		newData
	);
};

const shouldShowLoadedPrices = function(parentAcc, info) {
	// Weird case: no shipping method has been chosen and the type is bundle and
	// it's not an upgrade. The backend was passed a default one and has a guessed
	// shipping/tax computed, but the frontend shouldn't pretend this is correct.
	const scs = parentAcc.subChangeState;
	const methodForPrices = _.get(scs, "priceInfo.shipping.method", "");
	// Upgrade has weird prices, so always use the backend calculated ones
	if (scs.changeType === "upgrade" && getPricesLoadStatus(parentAcc) === "done")
		return true;
	if (!scs || (scs.newPlanType === "bundle" && !methodForPrices)) {
		return false;
	}
	return !!(info && info.totals);
};

const getPrices = function(parentAcc, final) {
	let info = getPricesCurrInfo(parentAcc);
	if (info && info.items) {
		info = setIn(info, ["items"], is =>
			stampDefermentOnItems(parentAcc, addDisplayNames(parentAcc, is))
		);
	}
	const showLoaded = shouldShowLoadedPrices(parentAcc, info);
	let prices = showLoaded
		? fixPrices(parentAcc, _.pick(info, ["items", "totals"]))
		: computeBasePrices(parentAcc);
	// Bit messy, but if the prices aren't done, don't show the tax to prevent
	// the user from thinking these are final.
	if (
		prices &&
		(!showLoaded ||
			getPricesLoadStatus(parentAcc) !== "done" ||
			needsBundleGrades(parentAcc))
	) {
		prices = setIn(prices, ["totals", "tax"], null);
	}
	if (prices && final) {
		prices = setIn(prices, "totals", t => _.mapValues(t, x => x || "0.00"));
	}
	return prices;
};

// Get prices for all newPlanTypes to show for choosing sub type
const getPricesForAll = function(parentAcc) {
	const scs = parentAcc.subChangeState;
	return _.get(scs, "priceInfo.forPlanType");
};

const getPricesLoadStatus = function(parentAcc) {
	const info = getPricesCurrInfo(parentAcc);
	const base = info ? info.load : "none";
	if (base !== "done" && areBasePricesComplete(parentAcc)) {
		return "done";
	} else if (base === "done" && needsToUpdatePricesShipping(parentAcc)) {
		return "none";
	}
	return base;
};

const getPricesLoadError = function(parentAcc) {
	const info = getPricesCurrInfo(parentAcc);
	return info ? info.loadError : null;
};

// TODO: Lots of duplication in OpsCart and OpsCartAPI. OpsCoupon maybe?

const setCoupon = function(parentAcc, coupon, api) {
	// Price updating uses the coupon, so these must happen sequentially.
	parentAcc = setIn(parentAcc, ["subChangeState", "coupon"], coupon);
	parentAcc = resetLoadedPrices(parentAcc, api);
	return parentAcc;
};

const getCouponCode = function(parentAcc) {
	return _.get(parentAcc, ["subChangeState", "coupon", "code"], null);
};

const getLastCouponCode = function(parentAcc) {
	return _.get(parentAcc, ["subChangeState", "lastCouponCode"], null);
};

const clearCoupon = function(parentAcc, api, options = {}) {
	const {noMemory = false} = options;
	const oldCode = getCouponCode(parentAcc);
	if (oldCode) {
		parentAcc = noMemory
			? parentAcc
			: setIn(parentAcc, ["subChangeState", "lastCouponCode"], oldCode);
		parentAcc = setCoupon(parentAcc, null, api);
	}
	return parentAcc;
};

const getCouponRequest = function(parentAcc, couponCode, api) {
	const scs = parentAcc.subChangeState;
	if (!scs || scs.isGettingCoupon) return parentAcc;
	api.getCouponForSubChange(couponCode);
	return setIn(parentAcc, ["subChangeState", "isGettingCoupon"], true);
};

const getCouponSuccess = function(parentAcc, response, emitEvent, api) {
	parentAcc = setIn(parentAcc, ["subChangeState", "isGettingCoupon"], false);
	if (response.coupon) {
		const planType = _.get(parentAcc, "subChangeState.newPlanType");
		const items = computeItems(parentAcc, planType);
		const couponStatus = CouponUtil.appliesToCart(response.coupon, items, true);
		if (!couponStatus.ok && couponStatus.error === "E_UNUSABLE_COUPON") {
			ErrorUtil.log(
				"E_UNUSABLE_COUPON",
				"Backend returned active coupon " +
					"that should be inactive (see CouponUtil.isUsableCoupon)",
				response.coupon
			);
			emitEvent("couponNotApplicable", couponStatus.error);
			parentAcc = setIn(
				parentAcc,
				["subChangeState", "lastCouponCode"],
				response.coupon.code
			);
		} else if (!couponStatus.ok) {
			emitEvent("couponNotApplicable", couponStatus.error);
			parentAcc = setIn(
				parentAcc,
				["subChangeState", "lastCouponCode"],
				response.coupon.code
			);
		} else {
			parentAcc = setCoupon(parentAcc, response.coupon, api);
			parentAcc = setIn(parentAcc, ["subChangeState", "lastCouponCode"], null);
			emitEvent("couponSuccess");
		}
	} else {
		ErrorUtil.log(
			"E_NO_COUPON_RESPONSE",
			`Coupon getting endpoint store:coupon:get ` +
				`did not have coupon in response`
		);
	}
	return parentAcc;
};

const getCouponFail = function(parentAcc, error, emitEvent) {
	parentAcc = setIn(parentAcc, ["subChangeState", "isGettingCoupon"], false);
	if (error === "E_INVALID_COUPON") {
		emitEvent("couponInvalid", error);
	} else {
		emitEvent("couponFail", error);
	}
	return parentAcc;
};

const getNewExpiration = function(parentAcc) {
	const info = getPricesCurrInfo(parentAcc);
	return info && info.expiresAt
		? info.expiresAt
		: computeBaseExpiresAt(parentAcc);
};

// May return null if not known
const getNewExpirationFormatted = function(parentAcc) {
	const expiresAtISO = getNewExpiration(parentAcc) || null;
	return expiresAtISO && moment.utc(expiresAtISO).format("MMM D, YYYY");
};

const getChangeWarningInfo = function(parentAcc) {
	const scs = parentAcc.subChangeState;
	if (!scs || !scs.studentID || scs.changeType === "add") {
		return null;
	}
	const currentSub = getCurrentSubscription(parentAcc);
	const currentExp = currentSub.expiresAt;
	const newExp = getNewExpiration(parentAcc);
	if (currentExp === newExp && scs.changeType === "upgrade") {
		return null;
	}
	//Show info about a delayed change if the expiration is staying the same
	//for an active subscription and the new plan type is not a bundle.
	if (
		currentSub.active &&
		currentExp === newExp &&
		scs.newPlanType !== "bundle"
	) {
		return {
			delayedChange: true,
			oldType: currentSub.planType,
			oldExpiresAt: moment.utc(currentExp).format("MMM D, YYYY"),
			newType: scs.newPlanType,
		};
	} else {
		return {
			delayedChange: false,
			newExpiresAt: getNewExpirationFormatted(parentAcc),
		};
	}
};

const getCouponExtraInfo = function(parentAcc) {
	const couponObj = _.get(parentAcc, ["subChangeState", "coupon"]);
	if (!couponObj) return;
	const balance = Big(couponObj.balance);
	const prices = getPrices(parentAcc);
	if (!prices) return;
	const subtotal = Big(prices.totals.subtotal || 0);
	const applied = Big(prices.totals.couponApplied || 0);
	return {
		couponUsed: !subtotal.eq(Big(0)),
		remainingBalance: balance.minus(applied),
	};
};

const willRenew = function(parentAcc) {
	const scs = parentAcc.subChangeState;
	if (!scs || !scs.newPlanType) {
		return false;
	} else if (scs.newPlanType === "bundle") {
		return false;
	} else if (scs.changeType === "switch") {
		const studentCR = getStudentCursor(parentAcc, scs.studentID);
		return studentCR.getIn(["subscription", "autoRenew"], true);
	}
	return true;
};

// Returns [non-multi, multi]. planType is optional and defaults to selected.
const getRenewPrices = function(parentAcc, planType) {
	const scs = parentAcc.subChangeState;
	if (!scs) {
		return null;
	}
	planType = planType || scs.newPlanType;
	if (!planType || planType === "bundle") {
		return null;
	} else {
		const sku = `beast-${planType}`;
		return [ShopData[sku].price, ShopData[sku].multiPrice].map(p =>
			Big(p).toFixed(2)
		);
	}
};

/**
 * Provides information to show for upgrading a subscription
 *
 * @param   {object}  parentAcc  parentAcc from the vault
 * @param   {string}  planType   monthly|yearly|bundle
 *
 * @return  {object}  upgradePrice: $ needed to pay to upgrade
											newExpiresAt: new expiration after upgrade
											upgradeUntil: last day to upgrade (from yearly)
 */
const getUpgradeInfo = function(parentAcc, planType) {
	const scs = parentAcc.subChangeState;
	const studentCR = getStudentCursor(parentAcc, scs.studentID);
	const priceInfo = getPricesForAll(parentAcc);
	planType = getPricesLoadKey(planType);
	const subPrice = _.get(
		priceInfo,
		[planType || scs.newPlanType, "totals", "subtotal"],
		0
	);
	const {lastRenewedAt, payments, planType: currPlanType} = studentCR.getIn([
		"subscription",
	]);
	const upgradeUntil =
		currPlanType === "yearly" ? moment(lastRenewedAt).add(1, "month") : null;
	// We use this instead of 11mo + expiresAt because we have to account for
	// monthly subscriptions that are pro-rated. We want to extend for a year when
	// they first bought, not 11mo after the monthly expires.
	const newExpiresAt =
		currPlanType === "monthly"
			? moment(_.get(payments, [0, "createdAt"])).add(1, "y")
			: null;
	return {
		upgradePrice: Big(subPrice),
		upgradeUntil,
		newExpiresAt,
	};
};

const getAllSteps = function(parentAcc) {
	if (needsShipping(parentAcc)) {
		return ["subscribe", "billing", "shipping", "review"];
	} else {
		return ["subscribe", "billing", "review"];
	}
};

// Internal helper for getAvailableSteps. Returns true if step past this one
// should be available.
const isStepComplete = function(parentAcc, step) {
	const scs = parentAcc.subChangeState;
	if (!scs) {
		return false;
	} else if (step === "subscribe") {
		return (
			scs.newPlanType &&
			!needsBundleGrades(parentAcc) &&
			!needsDisplayName(parentAcc)
		);
	} else if (step === "billing") {
		return !needsToAgreeToBilling(parentAcc);
	} else if (step === "shipping") {
		return (
			isShippingComplete(parentAcc) &&
			!needsToVerifyShipping(parentAcc) &&
			!shouldSaveShippingAsDefault(parentAcc)
		);
	}
	return true;
};

const getAvailableSteps = function(parentAcc) {
	const steps = getAllSteps(parentAcc);
	// TODO: remove international shipping block after the plague https://bugs.aops.com/T33397
	const blockedInternational = needsShippingToBlockedAddress(parentAcc);
	return _.takeWhile(
		steps,
		(s, i) =>
			i === 0 ||
			(isStepComplete(parentAcc, steps[i - 1]) &&
				(s !== "review" || !blockedInternational))
	);
};

// Internal helper
const getAPICallType = function(parentAcc) {
	const scs = parentAcc.subChangeState;
	if (!scs || !scs.newPlanType) {
		return null;
	} else if (scs.changeType === "add") {
		return "add";
	} else if (scs.changeType === "reactivate") {
		return "reactivate";
	} else if (scs.changeType === "renew") {
		return "renewBundle";
	} else if (scs.changeType === "switch") {
		return "switchType";
	} else if (scs.changeType === "upgrade") {
		return "upgrade";
	}
	return null;
};

const doFinishRequest = function(parentAcc, api) {
	const scs = parentAcc.subChangeState;
	let callType = getAPICallType(parentAcc);
	if (!callType || !scs) return parentAcc;
	if (scs.isRequestPending) return parentAcc;

	const shipping = getShipping(parentAcc);
	const prices = getPrices(parentAcc, true);
	const studentCR = getStudentCursor(parentAcc, scs.studentID);

	let subscriptionID = null;
	if (studentCR.exists()) {
		const subscription = studentCR.getIn("subscription");
		subscriptionID = studentCR.getIn(["subscription", "subscriptionID"]);
		const willExpireSoon = SubscriptionUtil.isActivePastExpiration(
			subscription
		);
		if (callType === "switchType" && willExpireSoon) {
			callType = "reactivate";
		}
	}

	const bundleGrades = getBundleGrades(parentAcc);
	const couponCode = getCouponCode(parentAcc);
	const bookLevelDeferred = getBookLevelDeferred(parentAcc);

	// This calls API.js subChange
	api.subChange({
		callType,
		subscriptionID,
		studentID: scs.studentID,
		planType: scs.newPlanType,
		displayName: scs.newDisplayName,
		grades: bundleGrades,
		shipping,
		prices: prices.totals,
		couponCode,
		bookLevelDeferred: bookLevelDeferred || undefined,
	});
	parentAcc = setIn(parentAcc, ["subChangeState", "isRequestPending"], true);
	return parentAcc;
};

// Returns the lastFinishedSCS shape.
const getFinishObject = function(parentAcc, orderID, newStudentID) {
	const scs = parentAcc.subChangeState;
	return {
		orderID: orderID || null,
		newStudentID: newStudentID || null,
		newStudentName: scs.newDisplayName,
		previousPlanType: _.get(getCurrentSubscription(parentAcc), "planType"),
		planType: scs.newPlanType,
		changeType: scs.changeType,
		needsShipping: needsShipping(parentAcc),
		priceInfo: getPrices(parentAcc, true),
		renew: willRenew(parentAcc),
		expiresAt: getNewExpirationFormatted(parentAcc),
		shownCC: parentAcc.shownCC,
		contact: OpsCheckoutForm.getSection(parentAcc.storedForm, "contact"),
		billing: OpsCheckoutForm.getSection(parentAcc.storedForm, "billing"),
		shipping: getShipping(parentAcc),
		cannotCancel: !canCancelSub(parentAcc),
		changeWarningInfo: getChangeWarningInfo(parentAcc),
		couponCode: getCouponCode(parentAcc),
		coupon: scs.coupon,
	};
};

const doFinishSuccess = function(parentAcc, data) {
	const scs = parentAcc.subChangeState;
	if (!scs) return parentAcc;
	parentAcc = setIn(parentAcc, ["subChangeState", "isRequestPending"], false);
	const finish = getFinishObject(parentAcc, data.orderID, data.studentID);
	parentAcc = setIn(parentAcc, ["lastFinishedSCS"], finish);
	parentAcc = resetState(parentAcc, scs.studentID, scs.changeType);
	parentAcc = markForReload(parentAcc);
	//Set up perm changes for parent, if necessary
	let parentPermUpdates = null;
	if (scs.changeType === "add") {
		parentPermUpdates = {
			[`student view#${data.studentID}`]: true,
			[`student lock#${data.studentID}`]: true,
		};
	}
	return {parentAcc, step: "confirm", parentPermUpdates};
};

// These errors indicate the frontend giving the backend bogus stuff. They are
// likely to indicate a serious issue in the frontend's payload computation.
const contractErrors = [
	"E_NOT_OWNER",
	"E_OWNER_NOT_FOUND",
	"E_NOT_PARENT",
	"E_INVALID_INPUT",
	"E_INVALID_BUNDLES",
	"E_INVALID_GRADE",
	"E_INVALID_OWNER",
	"E_INVALID_SUBSCRIPTION",
];
// These errors are similar, but can more easily happen if the frontend is
// using old data and needs to reload.
const syncErrors = [
	"E_ALREADY_REACTIVATED",
	"E_SUBSCRIPTION_CURRENT",
	"E_NOT_ACTIVE",
	"E_SAME_TYPE",
	"E_NOT_BUNDLED",
	"E_MAX_SUBSCRIPTIONS",
];

const doFinishError = function(parentAcc, emitEvent, api, error, errorInfo) {
	const scs = parentAcc.subChangeState;
	if (!scs) return parentAcc;
	parentAcc = setIn(parentAcc, ["subChangeState", "isRequestPending"], false);
	let step = null;
	let shouldLeave = false;

	if (error === "E_TOO_MANY_REQUESTS") {
		shouldLeave = true;
	} else if (error === "E_FIVE_YEARS") {
		shouldLeave = true;
		emitEvent("changeSubscriptionTooFarOut");
	} else if (error === "E_STUDENT_EXISTS") {
		step = "subscribe";
		parentAcc = markForReload(parentAcc);
		emitEvent("changeSubscriptionStudentExists");
	} else if (error === "E_CHECKOUT_PRICE_MISMATCH") {
		ErrorUtil.log(
			"E_SUBSCRIPTION_CHANGE_INVALID",
			`User got ${error} while trying to do ${scs.changeType} on student ${scs.studentID}`
		);
		parentAcc = resetLoadedPrices(parentAcc, api);
		emitEvent("changeSubscriptionPriceFail", error);

		// We don't currently handle these two kinds of errors differently, but it
		// seems likely that we might.
	} else if (
		contractErrors.indexOf(error) > -1 ||
		syncErrors.indexOf(error) > -1
	) {
		ErrorUtil.log(
			"E_SUBSCRIPTION_CHANGE_INVALID",
			`User got ${error} while trying to do ${scs.changeType} on student ${scs.studentID}`
		);
		shouldLeave = true;
		parentAcc = markForReload(parentAcc);
		emitEvent("changeSubscriptionInvalid", error);
	} else if (error === "E_CHECKOUT_CARD_DECLINED") {
		step = "billing";
		emitEvent("cardDeclined");
	} else if (error === "E_INVALID_CARD") {
		step = "billing";
		emitEvent("cardInvalid");
	} else if (error === "E_CHECKOUT_SERVER_TIMEOUT") {
		emitEvent("checkoutServerTimeout");
	} else if (error === "E_MULTIPLE_FAILED_CHECKOUTS") {
		emitEvent("multipleFailedCheckouts");
	} else if (
		error === "E_CHECKOUT_INPUT" ||
		error === "E_CHECKOUT_DUPLICATE" ||
		error === "E_SUSPECTED_FRAUD" ||
		error === "E_CHECKOUT_UNKNOWN"
	) {
		let reason;
		if (
			_.get(errorInfo, "creditCard") &&
			_.isArray(errorInfo.creditCard) &&
			_.includes(errorInfo.creditCard, "exp")
		) {
			reason = "cardExpired";
		}
		emitEvent("changeSubscriptionFail", reason);
	} else {
		// Timeout or unrecognized error code.
		shouldLeave = true;
		parentAcc = markForReload(parentAcc);
		emitEvent("changeSubscriptionUnknown", scs.studentID, scs.changeType);
	}
	return {parentAcc, step, shouldLeave};
};

module.exports = {
	syncToPage,
	getValidPlanTypes,
	needsShipping,
	setUsingAccountAddress,
	getShipping,
	needsShippingToBlockedAddress,
	editShipping,
	calculateShippingPriceWithMethod,
	validateShippingWithMissing,
	checkShipping,
	isShippingComplete,
	mustSaveShippingAsDefault,
	shouldSaveShippingAsDefault,
	setShippingIsNewDefault,
	needsToVerifyShipping,
	getFinishShippingAction,
	shouldBlockPage,
	setShippingSavingState,
	doVerifyShippingRequest,
	doVerifyShippingSuccess,
	doVerifyShippingFail,
	needsPlanType,
	getDisplayName,
	setDisplayName,
	canSetDisplayName,
	needsDisplayName,
	setPlanType,
	setBundleGrades,
	needsBundleGrades,
	setBookLevelDeferred,
	getBookLevelDeferred,
	setAgreedToBilling,
	canAgreeToBilling,
	needsToAgreeToBilling,
	canCancelSub,
	getCurrentSubscription,
	isNewBundlePlanType,
	getPricesLoadKey,
	loadPricesIfNeeded,
	resetLoadedPrices,
	setLoadedPricesSuccess,
	setLoadedPricesFail,
	setLoadedSwitchTypePricesSuccess,
	setLoadedSwitchTypePricesFail,
	getPrices,
	getPricesForAll,
	getPricesLoadStatus,
	getPricesLoadError,
	clearCoupon,
	getCouponCode,
	getLastCouponCode,
	getCouponRequest,
	getCouponSuccess,
	getCouponFail,
	getNewExpiration,
	getNewExpirationFormatted,
	getChangeWarningInfo,
	getCouponExtraInfo,
	willRenew,
	getRenewPrices,
	getUpgradeInfo,
	getAllSteps,
	getAvailableSteps,
	doFinishRequest,
	getFinishObject,
	doFinishSuccess,
	doFinishError,
};
